<script lang="ts" setup>
import moment from 'moment'
import { useSearch } from '~/store/search'
import dateLocale from '~/assets/data/date-localization.json'
import metas from '~/assets/data/search-meta.json'
import type { JobItem } from '~/models/Job'

const emit = defineEmits(['setMeta', 'tabTotal', 'currUrl', 'calculateOther'])
const route = useRoute()
const router = useRouter()
const { $config } = useNuxtApp()
const token = useCookie('token', { path: '/', domain: $config.public.COOKIE_DOMAIN })
const searchStore = useSearch()
const isLoading = ref(true)
const jobData = ref()
const newPayload = ref<any>(await searchStore.getPayload(route.params, route.query))
const locationList = ref()
const arrJob = ref(newPayload.value?.filter?.type || [])
const arrLocations = ref(newPayload.value?.filter?.locations || [])
const selWork = ref(newPayload.value?.filter?.remote || '')
const selDate = ref(newPayload.value?.filter?.date || '')
const selSort = ref(newPayload.value?.filter?.sort || '')
const openLocation = ref(false)
const currPage = ref<number>(!isEmpty(route.query?.page) ? parseInt(route.query?.page.toString()) : 1)
const countryID = useGetSubdomain()
const totalPagination = ref(Math.ceil(jobData.value?.total / newPayload.value?.filter?.size))
const metaTitle = ref('')

try {
  emit('calculateOther')
  jobData.value = {}
  isLoading.value = true
  const [result, locations] = await Promise.all([
    useAsyncData('get_job_list', () => searchStore.getJob(newPayload.value?.filter, $config.public.SEARCHAPI_BASE_URL, $config.SEARCHAPI_KEY, true), { server: true }),
    useAsyncData('get_loc_list', () => searchStore.getLoc('jobs', newPayload.value?.filter), { server: true }),
  ])

  const rawJob = result.data.value as { total: number; hits?: JobItem[] }
  if (rawJob?.hits)
    jobData.value = { job: rawJob?.hits, total: rawJob.total }

  const rawLoc = locations.data.value
  if (rawLoc)
    locationList.value = rawLoc

  updateMeta()
}
catch (error) {
  console.error('Error fetching data', error)
  showError({ message: 'Failed to load data', statusCode: 500 })
}

async function fetchJob() {
  jobData.value = {}
  isLoading.value = true
  const query = { keyword: newPayload.jobTitle, from: currPage.value, size: 21, locations: arrLocations.value, employment: arrJob.value, work_type: selWork.value, date_posted: selDate.value, sort: selSort.value }

  const result = await searchStore.getJob(query, $config.public.SEARCHAPI_BASE_URL, $config.SEARCHAPI_KEY, true)
  const rawJob = result as { total: number; hits?: JobItem[] }
  if (!isEmpty(rawJob?.hits)) {
    jobData.value = { job: rawJob?.hits, total: rawJob?.total }
    locationList.value = await searchStore.getLoc('jobs', query)
  }

  updateMeta()
  emit('currUrl', query)
}

async function updateFilter(page: number, isFilter = false, type = '') {
  // we have 3 type: null is for filters, reset coming from reset all btn, and other type that is the value of emptyList opts
  currPage.value = page
  openLocation.value = false
  let queryParam = ''

  if (type === 'reset') {
    arrJob.value = []
    selWork.value = ''
    selDate.value = ''
    arrLocations.value = []
    router.replace(route.path)
    fetchJob()
  }
  else {
    const updatedPayload = searchStore.extractJobInfo(route.params.search.toString())
    let path = route.path

    // SET THE QUERY
    if (!updatedPayload.workType && selWork.value) {
      if (Object.values(updatedPayload).every(value => value === null))
        path = useGenerateSearchUrl('', '', '', selWork.value)
      else
        queryParam = `${queryParam}&work_type=${selWork.value}`
    }
    if (!updatedPayload.jobType && arrJob.value.length > 0) {
      if (Object.values(updatedPayload).every(value => value === null) && arrJob.value.length === 1)
        path = useGenerateSearchUrl('', '', arrJob.value[0])
      else
        queryParam = `${queryParam}&employment=${arrJob.value.join(',')}`
    }
    if (!updatedPayload.location && arrLocations.value.length > 0) {
      if (Object.values(updatedPayload).every(value => value === null) && arrLocations.value.length === 1)
        path = useGenerateSearchUrl('', arrLocations.value[0])
      else
        queryParam = `${queryParam}&locations=${arrLocations.value.join(',')}`
    }
    if (selDate.value)
      queryParam = `${queryParam}&date_posted=${selDate.value}`
    if (selSort.value === 'date')
      queryParam = `${queryParam}&sort=${selSort.value}`

    // SET THE PATH
    if (updatedPayload.workType && selWork.value)
      path = path.replace(updatedPayload.workType, selWork.value)
    if (updatedPayload.jobType && arrJob.value.length > 0) {
      path = path.replace(`${updatedPayload.jobType}-`, '')
      queryParam = `${queryParam}&employment=${arrJob.value.join(',')}`
    }
    if (updatedPayload.location && arrLocations.value.length > 0) {
      path = path.replace(`${updatedPayload.location}-`, '')
      queryParam = `${queryParam}&locations=${arrLocations.value.join(',')}`
    }
    router.replace(`${path}${queryParam.replace('&', '?')}`)

    if (path === route.path)
      fetchJob()
  }
}

async function updateMeta() {
  const updatedPayload = searchStore.extractJobInfo(route.params.search.toString())
  let month = moment().format('MMMM')
  let rawMeta = {} as any
  let metaType = 'jobsearch'
  const metaDesc = ref('')
  const metaCanon = ref('')
  const metaRobot = jobData?.value?.total > 0 ? 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1' : 'nofollow, noindex'

  if (countryID === 'id') {
    rawMeta = metas[2]
    month = dateLocale[0]?.months[parseInt(moment().format('M')) - 1]
  }
  else if (countryID === 'my') {
    rawMeta = metas[1]
    month = dateLocale[1]?.months[parseInt(moment().format('M')) - 1]
  }
  else {
    rawMeta = metas[0]
  }

  if (updatedPayload.jobTitle && updatedPayload.location) {
    metaType += '-both'
  }
  else {
    if (updatedPayload.jobTitle)
      metaType += '-job'
    if (updatedPayload.location)
      metaType += '-loc'
  }

  if (!updatedPayload.jobTitle && !updatedPayload.location && (updatedPayload.jobType || updatedPayload.workType))
    metaType += '-only'

  if (updatedPayload.jobType)
    metaType += `-${updatedPayload.jobType.replace('-', '')}`
  if (updatedPayload.workType === 'remote')
    metaType += '-wfh'

  let selectedMeta = rawMeta.meta.find((obj: any) => obj.type === metaType)
  if (isEmpty(selectedMeta)) {
    selectedMeta = rawMeta.meta.find((obj: any) => obj.type === 'jobsearch-other')
    metaType = 'jobsearch-other'
  }

  metaTitle.value = selectedMeta?.title.replaceAll('{location}', useTextCapitalize(updatedPayload.location)).replaceAll('{job}', useTextCapitalize(metaType !== 'jobsearch-other' ? updatedPayload.jobTitle : `${updatedPayload.jobType ? updatedPayload.jobType.replace('-', '') : ''} ${updatedPayload.workType || ''}`)).replaceAll('{month}', useTextCapitalize(month)).replaceAll('{year}', moment().format('YYYY')).replaceAll('{domain}', useSetSubdomain(useGetSubdomain())) || ''
  metaDesc.value = selectedMeta?.desc.replaceAll('{location}', useTextCapitalize(updatedPayload.location)).replaceAll('{job}', useTextCapitalize(metaType !== 'jobsearch-other' ? updatedPayload.jobTitle : `${updatedPayload.jobType ? updatedPayload.jobType.replace('-', '') : ''} ${updatedPayload.workType || ''}`)).replaceAll('{month}', useTextCapitalize(month)).replaceAll('{year}', moment().format('YYYY')).replaceAll('{domain}', useSetSubdomain(useGetSubdomain())) || ''
  metaCanon.value = route.fullPath
  emit('setMeta', { title: metaTitle.value, desc: metaDesc.value, canon: metaCanon.value, robots: metaRobot })
  emit('tabTotal', jobData.value?.total)

  isLoading.value = false
  totalPagination.value = Math.ceil(jobData.value?.total / newPayload.value?.filter?.size)
}
</script>

<template>
  <div v-if="route.query?.st !== 'companies'">
    <div v-if="isLoading" class="grid gap-y-4 pb-60">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
        <BrowsejobsJobCard v-for="idx in 12" :key="idx" :is-loading="true" class="flex-none" />
      </div>
    </div>
    <div v-if="!isLoading" class="grid gap-y-4">
      <div v-if="jobData?.total > 0">
        <h1 class="text-xl lg:text-3xl font-bold mb-2 lg:mb-4">
          {{ metaTitle.split('-')[0] }}
        </h1>

        <span class="text-sm text-gray-600">
          Found {{ useCurrencyFormat(null, jobData?.total, null, false, true, false) }}
          <span class="capitalize">
            {{ !isEmpty(newPayload.filter?.type) ? newPayload.filter?.type.map(t => t.replace('_', '')).join(', ') : '' }}
            {{ newPayload.filter?.remote }}
          </span>
          Jobs
          <span class="capitalize">{{ !isEmpty(newPayload.filter?.keyword) ? ` Related to ${newPayload.filter?.keyword}` : '' }} {{ !isEmpty(newPayload.filter?.locations) ? `in ${newPayload.filter?.locations.join(', ')}` : '' }}</span>
        </span>
      </div>
      <!-- Filter Section -->
      <ClientOnly>
        <div class="z-20 snap snap-x snap-mandatory flex pb-2 lg:pb-0 max-w-screen overflow-x-scroll md:overflow-x-hidden flex">
          <div class="snap-start block lg:hidden mr-3">
            <!-- mobile filter -->
            <SearchFilterMobileJob
              :list-location="locationList"
              :ori-job="arrJob"
              :ori-work="selWork"
              :ori-date="selDate"
              :ori-locations="arrLocations"
              @update:job="item => { arrJob = item; updateFilter(1, true) }"
              @update:work="item => { selWork = item; updateFilter(1, true) }"
              @update:date="item => { selDate = item; updateFilter(1, true) }"
              @update:locations="item => { arrLocations = item; updateFilter(1, true) }"
              @open-location="val => { openLocation = val }"
            />
          </div>

          <!-- employement filter -->
          <div class="snap-start hidden lg:block mr-3">
            <SearchFilterJobType :ori-job="arrJob" @update:model-value="val => { arrJob = val, updateFilter(1, true) }" />
          </div>

          <!-- work type filter -->
          <div class="snap-start hidden lg:block mr-3">
            <SearchFilterWorkType :ori-work="selWork" @update:model-value="val => { selWork = val, updateFilter(1, true) }" />
          </div>

          <!-- date posted filter -->
          <div class="snap-start hidden lg:block mr-3">
            <SearchFilterDatePosted :ori-date="selDate" @update:model-value="val => { selDate = val, updateFilter(1, true) }" />
          </div>

          <!-- location filter -->
          <div class="snap-start hidden lg:block mr-3">
            <SearchFilterLocations :is-open="openLocation" :list="locationList" :ori-location="arrLocations" @update:model-value="val => { arrLocations = val, updateFilter(1, true) }" @update:open="val => { openLocation = val }" />
          </div>

          <!-- reset all btn -->
          <div v-if="!isEmpty(arrJob) && !isEmpty(selWork) && !isEmpty(selDate) && !isEmpty(arrLocations)" class="text-[#5843E5] text-sm cursor-pointer font-semibold my-auto" @click="updateFilter(1, true, 'reset')">
            Reset All
          </div>

          <SearchFilterSort class="!ml-auto" :ori-sort="selSort" @update:model-value="val => { selSort = val, updateFilter(1) }" />
        </div>
      </ClientOnly>

      <!-- Card Section -->
      <div v-if="!isEmpty(jobData?.job)" class="grid gap-y-10 lg:mt-2">
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          <BrowsejobsJobCard v-for="(jobItem, jobIdx) in jobData?.job" :key="jobIdx" impression-source="search-job" :keyword="newPayload?.keyword" :loc="arrLocations" :job="arrJob" :work="selWork" :date="selDate" :sort="selSort" source="searchPage" :item="jobItem" :job-id="jobItem.job_id" />
        </div>

        <div class="pl-0 flex-1 flex justify-end">
          <SearchPagination :curr-page="currPage" :total-pages="totalPagination" @change-page="value => updateFilter(value)" />
        </div>
      </div>
    </div>
    <div v-if="!isLoading && (isEmpty(jobData) || jobData?.job.length === 0)" class="grid md:grid-cols-12 gap-y-4 md:gap-x-6 lg:gap-x-14 mt-6 lg:mt-10 <lg:mb-20">
      <div class="col-span-12 md:col-span-7">
        <p class="text-xl lg:text-3xl font-bold mb-4">
          Sorry we couldn’t find
          <span class="capitalize">
            {{ !isEmpty(newPayload.filter?.type) ? newPayload.filter?.type.map(t => t.replace('_', '')).join(', ') : '' }}
            {{ newPayload.filter?.remote }}
          </span>
          Jobs
          <span class="capitalize">{{ !isEmpty(newPayload.filter?.keyword) ? ` Related to ${newPayload.filter?.keyword}` : '' }} {{ !isEmpty(newPayload.filter?.locations) ? `in ${newPayload.filter?.locations.join(', ')}` : '' }}</span>
        </p>
        <div class="mb-6 text-gray-600 text-sm">
          Tips on searching jobs :
          <ul class="list-disc ml-6 mb-4">
            <li>Make sure keywords is correct</li>
            <li>Use common jobs position like "Finance staff"</li>
            <li>Don't use abbreviations (e.g., "Manager" not "Mgr")</li>
            <li>Search wider locations</li>
            <li>Adjust filters to get better results</li>
          </ul>
          Can't find the right job? Let us recommend jobs that match your profile! Just click below to complete your profile and let the right jobs find you!
        </div>
        <div class="mb-6 pb-4 lg:py-4 w-full lg:max-w-xs">
          <BaseAtomsButton :to="!isEmpty(token) ? '/dashboard/job-recommendation/fetch' : '/continue?redirect_to=/dashboard/job-recommendation/fetch'" size="md" classes="font-semibold w-full mb-2">
            {{ !isEmpty(token) ? 'Go to Job Recommendation' : 'Find Me Jobs' }}
          </BaseAtomsButton>
          <p class="text-xs text-center">
            {{ !isEmpty(token) ? 'Start your dream career with us. Free Forever.' : 'Only take less than 5 mins. Free Forever.' }}
          </p>
        </div>
        <SearchPopularList />
      </div>
      <div v-if="useGetSubdomain() !== 'uk' && useGetSubdomain() !== 'cn' && useGetSubdomain() !== 'hk'" class="col-span-12 md:col-span-5 bg-white px-2 lg:px-6 py-6 lg:py-10 rounded-sm" style="height: fit-content">
        <p class="text-xl font-bold px-4 py-2 bg-[#eff6ff] mb-6 mx-auto" style="width: fit-content">
          Top Hiring Companies
        </p>
        <img class="mx-auto w-full max-w-[80%]" :src="`/images/search/empty_${useGetSubdomain()}.png`">
      </div>
    </div>
    <SearchArticlesSection v-if="jobData?.total > 0" :keyword="newPayload?.keyword" />
  </div>
</template>
